import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  useId,
  ToolbarButton,
  Tooltip,
  Input,
  Button,
  Badge,
} from '@fluentui/react-components';
import OrderStatusPill from '../components/OrderStatusPill';
import PaymentStatusPill from '../components/PaymentStatusPill';
import {
  PanelLeft24Regular,
  Search20Regular,
  VehicleTruckProfile24Regular,
  Add24Regular,
  DismissCircle20Regular,
  CheckmarkCircle20Filled,
} from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import TablePage from '../components/TablePage';
import OrderFiltersSidebar from '../components/OrderFiltersSidebar';
import { useOrderFilters } from '../hooks/useOrderFilters';
import { getRowMenuForRoute } from '../config/toolbars';
import { BOOQABLE_ADMIN_URL } from '../config/booqable';
import { fetchOrders, type Order } from '../services/orderService';
import { fetchCustomers, type Customer } from '../services/customerService';
import { queryKeys, queryClient } from '../lib/queryClient';
import { useDebug } from '../contexts/DebugContext';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import ShareDialog from '../components/ShareDialog';
import { apiFetch } from '../lib/apiFetch';
import { formatCalendarDate, formatBusinessDateTime } from '../lib/businessTime';

const useStyles = makeStyles({
  dateRangeButton: {
    width: '100%',
    justifyContent: 'flex-start',
    ...shorthands.padding('8px', '12px'),
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    ...shorthands.padding('20px'),
    minWidth: '680px',
    // 680px is the two-calendar layout (~340px each). Below 768px the
    // popover gets clamped to the viewport so it doesn't overflow off
    // screen; the dateColumns grid stacks the two calendars vertically.
    '@media (max-width: 768px)': {
      minWidth: 0,
      width: 'calc(100vw - 32px)',
      maxWidth: 'calc(100vw - 32px)',
      ...shorthands.padding('12px'),
    },
  },
  dateColumns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: '16px',
    },
  },
  dateColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#242424',
    marginBottom: '4px',
  },
  input: {
    width: '100%',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.border('1px', 'solid', '#d1d1d1'),
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    backgroundColor: '#ffffff',
    '&::placeholder': {
      color: '#999',
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 1px #0078d4',
    },
  },
  monthSelector: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    marginTop: '4px',
  },
  monthText: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#242424',
    cursor: 'pointer',
  },
  navButtons: {
    display: 'flex',
    gap: '4px',
  },
  navButton: {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    ...shorthands.padding('0'),
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
    marginBottom: '12px',
  },
  dayHeader: {
    fontSize: '12px',
    fontWeight: 600,
    textAlign: 'center',
    padding: '8px 4px',
    color: '#605e5c',
  },
  day: {
    fontSize: '14px',
    textAlign: 'center',
    ...shorthands.padding('8px', '4px'),
    cursor: 'pointer',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    color: '#242424',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: '#f3f2f1',
    },
  },
  dayOtherMonth: {
    color: '#a19f9d',
  },
  daySelected: {
    backgroundColor: '#0078d4',
    color: '#ffffff',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#106ebe',
    },
  },
  dayToday: {
    fontWeight: 600,
    ...shorthands.border('2px', 'solid', '#0078d4'),
  },
  calendarActions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  actionLink: {
    fontSize: '14px',
    color: '#0078d4',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding('4px', '0'),
    fontFamily: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    marginTop: '20px',
    paddingTop: '16px',
    ...shorthands.borderTop('1px', 'solid', '#edebe9'),
  },
  // Centered empty-state placeholder shown when there are no orders to list
  // (either none exist yet, or filters/search matched nothing).
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    flexGrow: 1,
    ...shorthands.padding('64px', '24px'),
  },
  emptyIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    marginBottom: '16px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    '& svg': {
      width: '28px',
      height: '28px',
    },
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '6px',
  },
  emptySubtext: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase300,
    maxWidth: '380px',
    marginBottom: '20px',
  },
  // "Pay methods" cell — lays the per-method badges in a row.
  payMethodsCell: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  // Green "invoice emailed" checkmark in the Orders list. Accent-only; blank
  // cell when no invoice has gone out yet.
  invoiceSentIcon: {
    color: tokens.colorPaletteGreenForeground1,
    display: 'block',
  },
});

const Orders: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);
  const { debugMode } = useDebug();
  const filters = useOrderFilters();
  const filterPanelCollapsed = filters.state.sidebarCollapsed;
  const toggleFilterPanel = () =>
    filters.setField('sidebarCollapsed', !filters.state.sidebarCollapsed);
  // Live filter for the table — narrows visible rows by order number,
  // customer name/email, location, etc. Distinct from the global topbar
  // search (cross-entity navigator).
  const [tableSearch, setTableSearch] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  // Orders the user just archived but whose archive round-trip hasn't reconciled
  // yet. Hidden from the list at the render level (below) so the row clears
  // instantly AND stays clear — a background /orders poll that lands mid-archive
  // would otherwise re-fetch the order while it's still `active` server-side and
  // make the row reappear. Cleared once the archive settles (the order comes
  // back tagged `archived`, which the default status filter hides) or on failure.
  const [pendingArchiveIds, setPendingArchiveIds] = useState<Set<string>>(new Set());
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [orderToArchive, setOrderToArchive] = useState<Order | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [orderToShare, setOrderToShare] = useState<Order | null>(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [orderToComplete, setOrderToComplete] = useState<Order | null>(null);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [orderToUnarchive, setOrderToUnarchive] = useState<Order | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Get row menu configuration for current route
  const rowMenuItems = getRowMenuForRoute(location.pathname);

  // Orders data via React Query
  const { data: orders = [], isLoading: loading, error: queryError } = useQuery({
    queryKey: queryKeys.orders.all,
    queryFn: fetchOrders,
    staleTime: 5 * 60 * 1000,
    // Near-live columns: poll so out-of-band changes (a pay-link payment,
    // another staff session changing status) surface within ~30s. Each poll is
    // cheap: /orders is a conditional GET (fetchOrders sends If-None-Match → 304
    // + one tiny aggregate on the common unchanged poll) and, when something did
    // change, a delta GET (?since) ships only the changed rows, not the whole
    // window. 30s (was 5s) trims the steady-state aggregate/connection churn —
    // every poll still pays a pooler round-trip — without losing near-live feel.
    // `refetchIntervalInBackground: false` still pauses polling on hidden tabs;
    // refetchOnWindowFocus (queryClient default) catches up on tab return.
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
  });
  // Customer list — needed for the customer-type filter (Order rows don't
  // carry kind directly; we resolve it via customerId). Cached separately
  // from the Customers page query (different key suffix); fine for v1.
  const { data: customersForLookup = [] } = useQuery<Customer[]>({
    queryKey: [...queryKeys.customers.all, 'orders-lookup'],
    queryFn: () => fetchCustomers(),
    staleTime: 10 * 60 * 1000,
  });
  const customerKindById = useMemo(() => {
    const map = new Map<string, Customer['customerKind']>();
    customersForLookup.forEach((c) => {
      if (c.id && c.customerKind) map.set(c.id, c.customerKind);
    });
    return map;
  }, [customersForLookup]);
  const error = queryError ? (queryError as Error).message : null;

  // Update content sidebar config with dynamic state
  useEffect(() => {
    // Notify Layout of config update
    window.dispatchEvent(new Event('contentSidebarConfigUpdate'));
  }, []);

  // Listen for Booqable sync trigger from toolbar
  useEffect(() => {
    const handleSync = async () => {
      if (syncing) return;
      setSyncing(true);
      dispatchToast(
        <Toast><ToastTitle>Syncing with Booqable...</ToastTitle></Toast>,
        { intent: 'info' }
      );
      try {
        const res = await apiFetch(`/orders/sync`, { method: 'POST' });
        const data = await res.json();
        if (data.success) {
          await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
          dispatchToast(
            <Toast>
              <ToastTitle>Sync complete</ToastTitle>
              <ToastBody>{data.created} created, {data.updated} updated</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        } else {
          dispatchToast(
            <Toast><ToastTitle>Sync failed</ToastTitle><ToastBody>{data.error}</ToastBody></Toast>,
            { intent: 'error' }
          );
        }
      } catch (e) {
        dispatchToast(
          <Toast><ToastTitle>Sync failed</ToastTitle><ToastBody>Network error</ToastBody></Toast>,
          { intent: 'error' }
        );
      } finally {
        setSyncing(false);
      }
    };

    window.addEventListener('syncBooqableOrders', handleSync);
    return () => window.removeEventListener('syncBooqableOrders', handleSync);
  }, [syncing, dispatchToast]);

  // Listen for archive order events
  useEffect(() => {
    const handleArchiveOrder = (event: CustomEvent) => {
      const order = event.detail;
      setOrderToArchive(order);
      setArchiveDialogOpen(true);
    };

    window.addEventListener('archiveOrder', handleArchiveOrder as EventListener);
    return () => {
      window.removeEventListener('archiveOrder', handleArchiveOrder as EventListener);
    };
  }, []);

  // Listen for share order events
  useEffect(() => {
    const handleShareOrder = (event: CustomEvent) => {
      const order = event.detail;
      setOrderToShare(order);
      setShareDialogOpen(true);
    };

    window.addEventListener('shareOrder', handleShareOrder as EventListener);
    return () => {
      window.removeEventListener('shareOrder', handleShareOrder as EventListener);
    };
  }, []);

  // Listen for cancel order events
  useEffect(() => {
    const handleCancelOrder = (event: CustomEvent) => {
      const order = event.detail;
      setOrderToCancel(order);
      setCancelDialogOpen(true);
    };

    window.addEventListener('cancelOrder', handleCancelOrder as EventListener);
    return () => {
      window.removeEventListener('cancelOrder', handleCancelOrder as EventListener);
    };
  }, []);

  // Listen for complete (stop) order events
  useEffect(() => {
    const handleCompleteOrder = (event: CustomEvent) => {
      setOrderToComplete(event.detail);
      setCompleteDialogOpen(true);
    };

    window.addEventListener('completeOrder', handleCompleteOrder as EventListener);
    return () => {
      window.removeEventListener('completeOrder', handleCompleteOrder as EventListener);
    };
  }, []);

  // Listen for un-archive (revert archived → stopped) order events
  useEffect(() => {
    const handleUnarchiveOrder = (event: CustomEvent) => {
      setOrderToUnarchive(event.detail);
      setUnarchiveDialogOpen(true);
    };

    window.addEventListener('unarchiveOrder', handleUnarchiveOrder as EventListener);
    return () => {
      window.removeEventListener('unarchiveOrder', handleUnarchiveOrder as EventListener);
    };
  }, []);

  // Handle archive confirmation
  const handleConfirmArchive = async () => {
    if (!orderToArchive) return;
    const archivedId = orderToArchive.id;

    // Hide the row immediately and durably via the render-level pending set —
    // this survives background /orders polls, unlike a one-shot setQueryData,
    // which a mid-archive refetch (order still `active` server-side) would undo.
    setPendingArchiveIds((prev) => new Set(prev).add(archivedId));
    setSelectedOrderIds(selectedOrderIds.filter(id => id !== archivedId));
    setArchiveDialogOpen(false);
    setOrderToArchive(null);

    const clearPending = () =>
      setPendingArchiveIds((prev) => {
        if (!prev.has(archivedId)) return prev;
        const next = new Set(prev);
        next.delete(archivedId);
        return next;
      });

    try {
      const response = await apiFetch(`/booqable/orders/${archivedId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || 'Failed to archive order');
      }

      dispatchToast(
        <Toast><ToastTitle>Order archived</ToastTitle></Toast>,
        { intent: 'success' },
      );
      // Reconcile, THEN drop the guard: once the refetch lands the order is
      // tagged `archived` and hidden by the default status filter, so there's no
      // window where it flashes back as active.
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      clearPending();
    } catch (error) {
      // Archive failed — stop suppressing the row so it reappears.
      clearPending();
      console.error('Error archiving order:', error);
      dispatchToast(
        <Toast><ToastTitle>Archive failed</ToastTitle><ToastBody>{(error as Error).message || 'Network error'}</ToastBody></Toast>,
        { intent: 'error' },
      );
    }
  };

  const handleConfirmCancel = async () => {
    if (!orderToCancel) return;

    try {
      const response = await apiFetch(`/booqable/orders/${orderToCancel.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const msg = body?.error || 'Failed to cancel order';
        dispatchToast(
          <Toast><ToastTitle>Cancel failed</ToastTitle><ToastBody>{msg}</ToastBody></Toast>,
          { intent: 'error' },
        );
        return;
      }

      setSelectedOrderIds(selectedOrderIds.filter(id => id !== orderToCancel.id));
      setCancelDialogOpen(false);
      setOrderToCancel(null);
      dispatchToast(
        <Toast><ToastTitle>Order canceled</ToastTitle></Toast>,
        { intent: 'success' },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      console.error('Error canceling order:', error);
      dispatchToast(
        <Toast><ToastTitle>Cancel failed</ToastTitle><ToastBody>Network error</ToastBody></Toast>,
        { intent: 'error' },
      );
    }
  };

  const handleConfirmComplete = async () => {
    if (!orderToComplete) return;

    try {
      const response = await apiFetch(`/booqable/orders/${orderToComplete.id}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const msg = body?.error || 'Failed to complete order';
        dispatchToast(
          <Toast><ToastTitle>Complete failed</ToastTitle><ToastBody>{msg}</ToastBody></Toast>,
          { intent: 'error' },
        );
        return;
      }

      setCompleteDialogOpen(false);
      setOrderToComplete(null);
      dispatchToast(
        <Toast><ToastTitle>Order completed</ToastTitle></Toast>,
        { intent: 'success' },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      console.error('Error completing order:', error);
      dispatchToast(
        <Toast><ToastTitle>Complete failed</ToastTitle><ToastBody>Network error</ToastBody></Toast>,
        { intent: 'error' },
      );
    }
  };

  const handleConfirmUnarchive = async () => {
    if (!orderToUnarchive) return;

    try {
      const response = await apiFetch(`/booqable/orders/${orderToUnarchive.id}/revert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Un-archive's one legal target is stopped (see ALLOWED_REVERTS).
        body: JSON.stringify({ to: 'stopped' }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const msg = body?.error || 'Failed to un-archive order';
        dispatchToast(
          <Toast><ToastTitle>Un-archive failed</ToastTitle><ToastBody>{msg}</ToastBody></Toast>,
          { intent: 'error' },
        );
        return;
      }

      setUnarchiveDialogOpen(false);
      setOrderToUnarchive(null);
      dispatchToast(
        <Toast><ToastTitle>Order un-archived</ToastTitle></Toast>,
        { intent: 'success' },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      console.error('Error un-archiving order:', error);
      dispatchToast(
        <Toast><ToastTitle>Un-archive failed</ToastTitle><ToastBody>Network error</ToastBody></Toast>,
        { intent: 'error' },
      );
    }
  };

  // Selected order objects + the subset Booqable will let us cancel. Cancel
  // is only legal from new/draft/reserved (mirrors the row-menu `hidden`
  // rule); started/stopped/canceled/archived rows are skipped.
  const selectedOrders = useMemo(() => {
    const ids = new Set(selectedOrderIds);
    return orders.filter((o) => ids.has(o.id));
  }, [orders, selectedOrderIds]);
  const cancellableSelected = useMemo(
    () =>
      selectedOrders.filter((o) =>
        ['new', 'draft', 'reserved'].includes((o as any).booqableStatus),
      ),
    [selectedOrders],
  );

  // Bulk-cancel: fan out the same per-order cancel endpoint, skip rows that
  // aren't in a cancellable state, and report a single summary toast.
  const handleConfirmBulkCancel = async () => {
    const targets = cancellableSelected;
    const skipped = selectedOrders.length - targets.length;
    setBulkCancelDialogOpen(false);
    if (targets.length === 0) return;

    const results = await Promise.allSettled(
      targets.map((o) =>
        apiFetch(`/booqable/orders/${o.id}/cancel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }).then(async (r) => {
          if (!r.ok) {
            const b = await r.json().catch(() => ({}));
            throw new Error(b?.error || 'Failed to cancel order');
          }
          return o.id;
        }),
      ),
    );

    const canceled = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - canceled;

    await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    setSelectedOrderIds([]);

    const parts = [`${canceled} canceled`];
    if (failed) parts.push(`${failed} failed`);
    if (skipped) parts.push(`${skipped} skipped (not cancellable)`);
    dispatchToast(
      <Toast>
        <ToastTitle>{failed ? 'Bulk cancel finished with errors' : 'Orders canceled'}</ToastTitle>
        <ToastBody>{parts.join(', ')}</ToastBody>
      </Toast>,
      { intent: failed ? 'warning' : 'success' },
    );
  };

  // Apply sidebar filters. Customer-type filter resolves order.customerId
  // through the cached customers list (built above into customerKindById).
  // Depend on the stable `applyFilters` callback (memoized in useOrderFilters on
  // the filter state) rather than the whole `filters` object, which is a fresh
  // literal every render and would otherwise re-run this filter over all orders
  // on every render.
  const filteredOrders = useMemo(
    () =>
      filters
        .applyFilters(orders, {
          getCustomerKind: (id) => (id ? customerKindById.get(id) : undefined),
        })
        // Suppress optimistically-archived rows until the archive reconciles.
        .filter((o) => !pendingArchiveIds.has(o.id)),
    [filters.applyFilters, orders, customerKindById, pendingArchiveIds],
  );

  // Apply the live search on top of the sidebar-filtered set.
  // Order number is matched exact (typing "1" should not surface #10, #11,
  // #100, etc.); all other fields are substring matches as you'd expect.
  // Typing a contact's name also hoists orders placed by their parent
  // company — same convenience as the customer picker.
  const searchedOrders = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return filteredOrders;

    // Companies whose contacts matched the query — their orders ride along.
    const hoistedCompanyIds = new Set<string>();
    for (const c of customersForLookup) {
      if (c.customerKind !== 'contact') continue;
      if (!c.linkedCompanyId) continue;
      const hits =
        (c.name || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q);
      if (hits) hoistedCompanyIds.add(c.linkedCompanyId);
    }

    return filteredOrders.filter((o) =>
      (o.orderNumber || '').toLowerCase() === q
      || (o.customerName || '').toLowerCase().includes(q)
      || (o.customerEmail || '').toLowerCase().includes(q)
      || (o.location || '').toLowerCase().includes(q)
      || (o.items || '').toLowerCase().includes(q)
      || (!!o.customerId && hoistedCompanyIds.has(o.customerId)),
    );
  }, [filteredOrders, tableSearch, customersForLookup]);

  // Define columns for DataTable
  const orderColumns: ColumnConfig<Order>[] = [
    {
      columnKey: 'checkbox',
      label: '',
      width: '50px',
    },
    {
      columnKey: 'orderNumber',
      label: 'Order #',
      sortable: true,
      render: (order) => (
        <RouterLink
          to={`/orders/${order.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            fontWeight: 500,
            color: 'inherit',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
          }}
        >
          {order.orderNumber}
        </RouterLink>
      ),
    },
    {
      columnKey: 'customerName',
      label: 'Customer',
      sortable: false,
      render: (order) => order.customerName,
    },
    // Project column retired — order phases supersede projects; hidden from the orders table.
    // {
    //   columnKey: 'project',
    //   label: 'Project',
    //   sortable: false,
    //   // Parent project for phase orders; links to the project in a NEW tab so
    //   // the staff member keeps their place in the orders list. Em dash for
    //   // one-off (non-project) orders.
    //   render: (order) => {
    //     if (!order.project) return '—';
    //     const label =
    //       order.project.name ||
    //       (order.project.projectNumber != null ? `P-${order.project.projectNumber}` : 'Project');
    //     return (
    //       <a
    //         href={`/projects/${order.project.id}`}
    //         target="_blank"
    //         rel="noopener noreferrer"
    //         onClick={(e) => e.stopPropagation()}
    //         style={{
    //           color: tokens.colorBrandForeground1,
    //           textDecoration: 'none',
    //         }}
    //         onMouseEnter={(e) => {
    //           (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
    //         }}
    //         onMouseLeave={(e) => {
    //           (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
    //         }}
    //       >
    //         {label}
    //       </a>
    //     );
    //   },
    // },
    {
      columnKey: 'booqableStatus',
      label: 'Order Status',
      sortable: false,
      render: (order) => <OrderStatusPill status={order.booqableStatus} />,
    },
    {
      columnKey: 'startDate',
      label: 'From',
      sortable: true,
      render: (order) => formatCalendarDate(order.startDate),
    },
    {
      columnKey: 'endDate',
      label: 'Until',
      sortable: true,
      render: (order) => formatCalendarDate(order.endDate),
    },
    {
      columnKey: 'price',
      label: 'Price',
      sortable: true,
      render: (order) => `$${order.price.toFixed(2)}`,
    },
    {
      columnKey: 'paymentStatus',
      label: 'Payment Status',
      sortable: false,
      render: (order) => <PaymentStatusPill status={order.paymentStatus} />,
    },
    {
      columnKey: 'paymentMethods',
      label: 'Pay methods',
      sortable: false,
      // Read-only mirror of the customer's allowed pay-link methods (resolved
      // in mapDbOrder; always non-empty, card-only default). ACH is a filled
      // brand badge so ACH-enabled / ACH-only customers pop while scanning;
      // Card stays a subtle tint.
      render: (order) => (
        <span className={styles.payMethodsCell}>
          {order.paymentMethods.map((m) => (
            <Badge
              key={m}
              size="small"
              appearance={m === 'ach' ? 'filled' : 'tint'}
              color={m === 'ach' ? 'brand' : 'informative'}
            >
              {m === 'ach' ? 'ACH' : 'Card'}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      columnKey: 'invoiceSent',
      label: 'Invoice sent',
      sortable: true,
      // Green checkmark once an invoice PDF has been emailed for the order
      // (tracked in email_sends); blank cell otherwise.
      render: (order) =>
        order.invoiceSent ? (
          <Tooltip content="Invoice sent to customer" relationship="label">
            <CheckmarkCircle20Filled
              className={styles.invoiceSentIcon}
              aria-label="Invoice sent"
            />
          </Tooltip>
        ) : null,
    },
    {
      columnKey: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (order) => formatBusinessDateTime(order.createdAt),
    },
    {
      columnKey: 'source',
      label: 'Source',
      sortable: true,
      // Origin channel. Legacy rows (backfilled 'booqable_sync') and any future
      // null read as '—'.
      render: (order) => {
        const labels: Record<string, string> = {
          website: 'Website',
          admin: 'Admin app',
          booqable_sync: 'Booqable',
        };
        return order.source ? (labels[order.source] ?? order.source) : '—';
      },
    },
    {
      columnKey: 'createdByName',
      label: 'Created by',
      sortable: true,
      // Staff name for admin-created orders; '—' for website/sync (no actor).
      render: (order) => order.createdByName || '—',
    },
    {
      columnKey: 'lastModified',
      label: 'Last modified',
      sortable: true,
      // Em dash when never edited in-app (null) so the column reads as "no
      // manual edits yet" rather than a blank that looks like a bug. Tracks
      // only user edits, not background syncs.
      render: (order) => (order.lastModified ? formatBusinessDateTime(order.lastModified) : '—'),
    },
  ];

  // No-orders-yet first-run state (vs. the "no matches" state, which keeps the
  // search bar so the user can widen their query). When no orders exist we omit
  // the search row + bulk actions entirely and let this fill the content column.
  const noOrdersYet = !loading && !error && orders.length === 0;

  return (
    <>
      <TablePage
        title="Orders"
        hideSelectionInfo
        loading={loading}
        loadingLabel="Loading orders…"
        error={error}
        headerLeftActions={
          <Tooltip
            content={filterPanelCollapsed ? 'Show filters' : 'Hide filters'}
            relationship="label"
          >
            <ToolbarButton
              icon={<PanelLeft24Regular />}
              onClick={toggleFilterPanel}
              aria-label={filterPanelCollapsed ? 'Show filters' : 'Hide filters'}
            />
          </Tooltip>
        }
        sidebar={
          <OrderFiltersSidebar
            filters={filters}
            orders={orders}
            customerKindById={customerKindById}
            collapsed={filterPanelCollapsed}
          />
        }
        searchRow={
          noOrdersYet ? undefined : (
            <>
              <Input
                size="large"
                contentBefore={<Search20Regular />}
                placeholder="Filter orders by #, customer, location, items…"
                value={tableSearch}
                onChange={(_e, data) => setTableSearch(data.value)}
                style={{ flexGrow: 1, maxWidth: '480px' }}
              />
              {tableSearch.trim() && selectedOrderIds.length === 0 && (
                <span
                  style={{
                    fontSize: '13px',
                    color: tokens.colorNeutralForeground3,
                    marginLeft: 'auto',
                  }}
                >
                  {searchedOrders.length} of {filteredOrders.length}
                </span>
              )}
            </>
          )
        }
        selectedCount={selectedOrderIds.length}
        onClearSelection={() => setSelectedOrderIds([])}
        bulkNoun={{ singular: 'order', plural: 'orders' }}
        bulkActions={
          noOrdersYet
            ? undefined
            : [
                {
                  key: 'cancel',
                  label: 'Cancel orders',
                  icon: <DismissCircle20Regular />,
                  onClick: () => setBulkCancelDialogOpen(true),
                  // Nothing in the selection is in a cancellable state.
                  disabled: cancellableSelected.length === 0,
                },
              ]
        }
      >
        {noOrdersYet ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <VehicleTruckProfile24Regular />
            </div>
            <div className={styles.emptyTitle}>No orders yet</div>
            <div className={styles.emptySubtext}>
              When a customer books a dumpster, their order shows up here.
              Create your first order to get rolling.
            </div>
            <Button
              appearance="primary"
              icon={<Add24Regular />}
              onClick={() => navigate('/orders/new')}
            >
              Add order
            </Button>
          </div>
        ) : searchedOrders.length > 0 ? (
          <DataTable
            data={searchedOrders}
            columns={orderColumns}
            keyField="id"
            selectedKeys={selectedOrderIds}
            onSelectionChange={setSelectedOrderIds}
            itemsPerPage={25}
            showCheckboxes={true}
            rowMenuItems={rowMenuItems || undefined}
            primaryColumnKey="orderNumber"
            disableWrapper={true}
            hideableColumns={true}
            columnVisibilityStorageKey="orders"
            reorderableColumns={true}
            columnOrderStorageKey="orders"
            columnWidthsStorageKey="orders"
          />
        ) : (
          // Orders exist, but the active filters/search hid them all.
          // Offer a one-click reset rather than a dead end.
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <Search20Regular />
            </div>
            <div className={styles.emptyTitle}>No matching orders</div>
            <div className={styles.emptySubtext}>
              No orders match your current filters and search. Try
              broadening them to see more.
            </div>
            <Button
              appearance="secondary"
              onClick={() => {
                filters.clearAll();
                setTableSearch('');
              }}
            >
              Clear filters &amp; search
            </Button>
          </div>
        )}
      </TablePage>

      {/* Archive Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={archiveDialogOpen}
        onClose={() => {
          setArchiveDialogOpen(false);
          setOrderToArchive(null);
        }}
        onConfirm={handleConfirmArchive}
        title="Archive Order"
        message={`Are you sure you want to archive order ${orderToArchive?.orderNumber}?`}
        subtext="This action is permanent"
        confirmButtonText="Archive"
        variant="danger"
      />

      {/* Cancel Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setOrderToCancel(null);
        }}
        onConfirm={handleConfirmCancel}
        title="Cancel Order"
        message={`Are you sure you want to cancel order ${orderToCancel?.orderNumber}?`}
        subtext="The order will be marked canceled."
        confirmButtonText="Cancel Order"
        variant="danger"
      />

      {/* Bulk Cancel Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={bulkCancelDialogOpen}
        onClose={() => setBulkCancelDialogOpen(false)}
        onConfirm={handleConfirmBulkCancel}
        title="Cancel orders"
        message={`Cancel ${cancellableSelected.length} ${cancellableSelected.length === 1 ? 'order' : 'orders'}?`}
        subtext={
          selectedOrders.length > cancellableSelected.length
            ? `${selectedOrders.length - cancellableSelected.length} selected order(s) can't be canceled (already started, stopped, or canceled) and will be skipped.`
            : 'The orders will be marked canceled.'
        }
        confirmButtonText="Cancel orders"
        variant="danger"
      />

      {/* Complete (stop) Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={completeDialogOpen}
        onClose={() => {
          setCompleteDialogOpen(false);
          setOrderToComplete(null);
        }}
        onConfirm={handleConfirmComplete}
        title="Complete Order"
        message={`Mark order ${orderToComplete?.orderNumber} as completed?`}
        subtext="This returns the items and stops the rental."
        confirmButtonText="Complete"
      />

      {/* Un-archive Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={unarchiveDialogOpen}
        onClose={() => {
          setUnarchiveDialogOpen(false);
          setOrderToUnarchive(null);
        }}
        onConfirm={handleConfirmUnarchive}
        title="Un-archive Order"
        message={`Un-archive order ${orderToUnarchive?.orderNumber}?`}
        subtext="The order returns to Completed status."
        confirmButtonText="Un-archive"
      />

      {/* Share Order Dialog */}
      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setOrderToShare(null);
        }}
        itemType="order"
        itemName={orderToShare?.orderNumber || ''}
        shareUrl={`${BOOQABLE_ADMIN_URL}/orders/${orderToShare?.booqable_id || orderToShare?.id}`}
        onCopyLink={() => {
          dispatchToast(
            <Toast>
              <ToastTitle>Link copied</ToastTitle>
              <ToastBody>Share link has been copied to clipboard.</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        }}
      />

      <Toaster toasterId={toasterId} />
    </>
  );
};

export default Orders;
